import {

    doc,

    collection,

    runTransaction,

    serverTimestamp,

    getDoc,

    getDocs,

    query,

    where,

    orderBy,

    arrayUnion,

    increment,

    limit,

    startAfter,

} from "firebase/firestore";

import { db }
    from "./firebase";

import {

    getTodayET,

    isFutureSlotET,

} from "../utils/timeUtils";

/* ---------------------------------- */
/* CONSTANTS */
/* ---------------------------------- */

const DAILY_LIMIT_MINUTES = 120;

const PAGE_SIZE = 10;

/* ---------------------------------- */
/* HELPERS */
/* ---------------------------------- */

function getUsageDocId(
    userId,
    date
) {

    return `${userId}_${date}`;
}

/* ---------------------------------- */
/* CHECK IF SLOT IS FUTURE */
/* ---------------------------------- */

function isFutureSlot(
    slot
) {

    if (!slot?.date) {

        return false;
    }

    return isFutureSlotET(
        slot.date,
        slot.endHour || (
            (slot.startHour || 0) +
            (slot.duration || 1)
        )
    );
}

/* ---------------------------------- */
/* CREATE BOOKING */
/* ---------------------------------- */

export async function createBooking({

    user,

    cartItems,

}) {

    if (!user) {

        throw new Error(
            "User not logged in"
        );
    }

    if (!cartItems?.length) {

        throw new Error(
            "Cart is empty"
        );
    }

    const bookingRef =
        doc(
            collection(
                db,
                "csc_bookings"
            )
        );

    try {

        await runTransaction(

            db,

            async (
                transaction
            ) => {

                const validatedSlots = [];

                /* ================================== */
                /* GROUP SLOTS BY DATE */
                /* ================================== */

                const dateMinutesMap = {};

                for (
                    const item
                    of cartItems
                ) {

                    const date =
                        item.date;

                    if (
                        !dateMinutesMap[date]
                    ) {

                        dateMinutesMap[date] = 0;
                    }

                    dateMinutesMap[date] +=
                        (
                            item.duration || 1
                        ) * 60;
                }

                /* ================================== */
                /* VALIDATE DAILY LIMITS */
                /* ================================== */

                for (
                    const date
                    of Object.keys(
                        dateMinutesMap
                    )
                ) {

                    const requestedMinutes =
                        dateMinutesMap[date];

                    const usageDocId =
                        getUsageDocId(
                            user.uid,
                            date
                        );

                    const usageRef =
                        doc(

                            db,

                            "csc_user_daily_usage",

                            usageDocId
                        );

                    const usageSnap =

                        await transaction.get(
                            usageRef
                        );

                    const currentMinutes =

                        usageSnap.exists()

                            ? usageSnap.data()
                                .bookedMinutes || 0

                            : 0;

                    const newTotalMinutes =

                        currentMinutes +
                        requestedMinutes;

                    if (

                        newTotalMinutes >
                        DAILY_LIMIT_MINUTES

                    ) {

                        throw new Error(

                            "Daily booking limit exceeded."
                        );
                    }
                }

                /* ================================== */
                /* VALIDATE SLOTS */
                /* ================================== */

                for (
                    const item
                    of cartItems
                ) {

                    const slotRef =
                        doc(

                            db,

                            "csc_slots",

                            item.slotId
                        );

                    const slotSnap =

                        await transaction.get(
                            slotRef
                        );

                    if (
                        !slotSnap.exists()
                    ) {

                        throw new Error(

                            `Slot missing: ${item.timeSlot}`
                        );
                    }

                    const slotData =
                        slotSnap.data();

                    const isBooked =

                        slotData.status ===
                        "booked";

                    if (isBooked) {

                        if (

                            slotData.bookedBy ===
                            user.uid

                        ) {

                            throw new Error(

                                "You already booked this slot."
                            );
                        }

                        throw new Error(

                            `${item.timeSlot} already booked`
                        );
                    }

                    validatedSlots.push({

                        slotRef,

                        item,
                    });
                }

                /* ================================== */
                /* BOOK SLOTS */
                /* ================================== */

                for (
                    const slot
                    of validatedSlots
                ) {

                    transaction.update(

                        slot.slotRef,

                        {

                            status:
                                "booked",

                            bookedBy:
                                user.uid,

                            bookingId:
                                bookingRef.id,

                            bookedAt:
                                serverTimestamp(),

                            updatedAt:
                                serverTimestamp(),
                        }
                    );
                }

                /* ================================== */
                /* UPDATE DAILY USAGE */
                /* ================================== */

                for (
                    const date
                    of Object.keys(
                        dateMinutesMap
                    )
                ) {

                    const requestedMinutes =
                        dateMinutesMap[date];

                    const usageDocId =
                        getUsageDocId(
                            user.uid,
                            date
                        );

                    const usageRef =
                        doc(

                            db,

                            "csc_user_daily_usage",

                            usageDocId
                        );

                    transaction.set(

                        usageRef,

                        {

                            userId:
                                user.uid,

                            date,

                            bookedMinutes:
                                increment(
                                    requestedMinutes
                                ),

                            bookingIds:
                                arrayUnion(
                                    bookingRef.id
                                ),

                            updatedAt:
                                serverTimestamp(),
                        },

                        {
                            merge: true,
                        }
                    );
                }

                /* ================================== */
                /* TOTAL */
                /* ================================== */

                const totalAmount =

                    cartItems.reduce(

                        (
                            sum,
                            item
                        ) =>

                            sum +
                            item.price,

                        0
                    );

                /* ================================== */
                /* CREATE BOOKING DOC */
                /* ================================== */

                transaction.set(

                    bookingRef,

                    {

                        bookingId:
                            bookingRef.id,

                        userId:
                            user.uid,

                        userName:
                            user.name || "",

                        userEmail:
                            user.email || "",

                        userPhone:
                            user.phone || "",

                        organization:
                            user.organization || "",

                        slots:
                            cartItems,

                        totalAmount,

                        bookingStatus:
                            "confirmed",

                        createdAt:
                            serverTimestamp(),
                    }
                );

                /* ================================== */
                /* CLEAR CART */
                /* ================================== */

                const cartRef =
                    doc(

                        db,

                        "csc_cart",

                        user.uid
                    );

                transaction.set(

                    cartRef,

                    {

                        items: [],

                        updatedAt:
                            serverTimestamp(),
                    },

                    {
                        merge: true,
                    }
                );
            }
        );

        return {

            success: true,

            bookingId:
                bookingRef.id,
        };

    } catch (error) {

        console.error(

            "Booking transaction failed:",

            error
        );

        throw error;
    }
}

/* ---------------------------------- */
/* GET BOOKED MINUTES */
/* ---------------------------------- */

export async function
    getBookedMinutesForDate({

        userId,

        date,
    }) {

    try {

        const usageDocId =
            getUsageDocId(
                userId,
                date
            );

        const usageRef =
            doc(

                db,

                "csc_user_daily_usage",

                usageDocId
            );

        const usageSnap =
            await getDoc(
                usageRef
            );

        if (
            !usageSnap.exists()
        ) {

            return 0;
        }

        return (
            usageSnap.data()
                .bookedMinutes || 0
        );

    } catch (error) {

        console.error(

            "Failed loading usage:",

            error
        );

        return 0;
    }
}

/* ---------------------------------- */
/* GET USER ORDER HISTORY */
/* ---------------------------------- */

export async function
    getUserOrders({

        userId,

        status = "all",

        dateFilter = "all",

        lastDoc = null,
    }) {

    try {

        const bookingsRef =
            collection(
                db,
                "csc_bookings"
            );

        const constraints = [

            where(
                "userId",
                "==",
                userId
            ),
        ];

        /* ---------------------------------- */
        /* STATUS FILTER */
        /* ---------------------------------- */

        const normalizedStatus =
            status?.toLowerCase();

        if (
            normalizedStatus !== "all"
        ) {

            constraints.push(

                where(
                    "bookingStatus",
                    "==",
                    normalizedStatus
                )
            );
        }

        /* ---------------------------------- */
        /* DATE FILTER */
        /* ---------------------------------- */

        if (
            dateFilter !== "all"
        ) {

            let days = 0;

            if (
                dateFilter === "week"
            ) {

                days = 7;
            }

            if (
                dateFilter === "month"
            ) {

                days = 30;
            }

            if (
                dateFilter === "3months"
            ) {

                days = 90;
            }

            const todayET =
                getTodayET();

            const startDateET =
                new Date(
                    `${todayET}T00:00:00`
                );

            startDateET.setDate(
                startDateET.getDate() - days
            );

            constraints.push(

                where(
                    "createdAt",
                    ">=",
                    startDateET
                )
            );
        }

        /* ---------------------------------- */
        /* ORDER */
        /* ---------------------------------- */

        constraints.push(

            orderBy(
                "createdAt",
                "desc"
            )
        );

        /* ---------------------------------- */
        /* PAGINATION */
        /* ---------------------------------- */

        if (lastDoc) {

            constraints.push(
                startAfter(lastDoc)
            );
        }

        constraints.push(
            limit(PAGE_SIZE)
        );

        const q =
            query(

                bookingsRef,

                ...constraints
            );

        const snapshot =
            await getDocs(q);

        const orders =
            snapshot.docs.map(
                (doc) => ({

                    id: doc.id,

                    ...doc.data(),
                })
            );

        return {

            orders,

            lastDoc:
                snapshot.docs[
                snapshot.docs.length - 1
                ] || null,

            hasMore:
                snapshot.docs.length ===
                PAGE_SIZE,
        };

    } catch (error) {

        console.error(

            "Failed loading user orders:",

            error
        );

        return {

            orders: [],

            lastDoc: null,

            hasMore: false,
        };
    }
}

/* ---------------------------------- */
/* GET UPCOMING BOOKINGS */
/* ---------------------------------- */

export async function
    getUpcomingBookings({

        userId,
    }) {

    try {

        const bookingsRef =
            collection(
                db,
                "csc_bookings"
            );

        const q =
            query(

                bookingsRef,

                where(
                    "userId",
                    "==",
                    userId
                ),

                where(
                    "bookingStatus",
                    "==",
                    "confirmed"
                ),

                orderBy(
                    "createdAt",
                    "desc"
                )
            );

        const snapshot =
            await getDocs(q);

        const orders =
            snapshot.docs
                .map((doc) => ({

                    id: doc.id,

                    ...doc.data(),
                }))

                .filter((booking) => {

                    const slots =
                        booking.slots || [];

                    return slots.some(
                        (slot) =>
                            isFutureSlot(slot)
                    );
                });

        return {

            orders,
        };

    } catch (error) {

        console.error(

            "Failed loading upcoming bookings:",

            error
        );

        return {

            orders: [],
        };
    }
}